import $ from '@rover/globals/jquery';

// Bootstrap's popover owns `aria-describedby` outright: showing one overwrites
// the attribute and hiding one removes it, so one invalid email would otherwise
// disconnect the "Last used" badge from its field for good. Record the badge id
// up front, then re-assert it after each validation hook -- keeping the
// popover's own id alongside it while an error is on screen, so a screen reader
// gets both the error and the badge.
const BADGE_ID_DATA_KEY = 'lastUsedBadgeId';

export const rememberBadgeDescriptions = ($scope: JQuery): void => {
  $scope.find('[aria-describedby]').each((idx, elt) => {
    const $el = $(elt);
    const badgeId = $el.attr('aria-describedby');
    if (badgeId) {
      $el.data(BADGE_ID_DATA_KEY, badgeId);
    }
  });
};

export const keepBadgeDescribed = ($el: JQuery): void => {
  const badgeId = $el.data(BADGE_ID_DATA_KEY);
  if (!badgeId) {
    return;
  }

  const ids = ($el.attr('aria-describedby') || '').split(/\s+/).filter(Boolean);
  if (!ids.includes(badgeId)) {
    $el.attr('aria-describedby', ids.concat(badgeId).join(' '));
  }
};

interface BadgePreservingValidationOptions {
  errorPlacement(this: unknown, $error: JQuery, $el: JQuery): void;
  success(this: unknown, $label: JQuery, element: HTMLElement): void;
}

// Wrap rather than replace: the popover behaviour is the global default every
// other form gets, and it's what writes `aria-describedby` in the first place.
// The two hooks reach the field differently -- jQuery Validate passes
// `errorPlacement` the field as a jQuery object, but hands `success` the error
// label first and the field second, as a raw DOM node.
export const badgePreservingValidationOptions = (): BadgePreservingValidationOptions => {
  const noop = (): void => {};
  // Callers build these options before knowing whether a form needs them, so a
  // page without the validate plugin must get harmless wrappers rather than a
  // throw that takes down the rest of its setup. `forms.js` guards the same way
  // before installing the defaults these wrap.
  const { errorPlacement = noop, success = noop } = ($ as any).validator?.defaults ?? {};

  return {
    errorPlacement($error, $el) {
      errorPlacement.call(this, $error, $el);
      keepBadgeDescribed($el);
    },
    success($label, element) {
      success.call(this, $label, element);
      keepBadgeDescribed($(element));
    },
  };
};
